import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/primitives";
import { IMAGINE_POINTS, IMAGES } from "@/data/content";

export default function ImagineTomorrow() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);

    return (
        <section className="border-t border-border py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <Eyebrow>Imagine Tomorrow</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 text-balance text-4xl font-bold leading-[1.08] tracking-tight md:text-5xl">
                                Imagine your operations team tomorrow
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-lg font-medium text-muted-foreground">
                                Imagine if…
                            </p>
                        </Reveal>

                        <ul className="mt-6 space-y-4">
                            {IMAGINE_POINTS.map((p, i) => (
                                <Reveal key={p} delay={0.12 + i * 0.07}>
                                    <li className="flex items-start gap-3">
                                        <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-navy/10 text-navy">
                                            <Check
                                                className="h-3 w-3"
                                                strokeWidth={2.5}
                                            />
                                        </span>
                                        <span className="text-lg leading-snug text-foreground">
                                            {p}
                                        </span>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>

                        <Reveal delay={0.5}>
                            <p className="mt-8 text-lg font-semibold text-foreground">
                                That&apos;s the future we help build.
                            </p>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <div
                                ref={ref}
                                className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-border"
                            >
                                <motion.img
                                    src={IMAGES.warehouse}
                                    alt="Automated logistics operation"
                                    loading="lazy"
                                    style={{ y, scale }}
                                    className="h-full w-full object-cover grayscale transition-[filter] duration-700 ease-premium hover:grayscale-0"
                                />
                                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand/55 via-transparent to-transparent" />
                                <div className="absolute bottom-5 left-5 rounded-2xl border border-white/15 bg-black/30 px-5 py-3 backdrop-blur-md">
                                    <span className="font-mono text-xs uppercase tracking-[0.16em] text-white/80">
                                        Less administration · More decisions
                                    </span>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
